<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  teams: {
    type: Array,
    default: () => [],
  },
  activeStatus: {
    type: String,
    default: 'open',
  },
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['apply', 'clear']);
const { t } = useI18n();
const [showDropdown, toggleDropdown] = useToggle(false);

const selectedStartDate = ref('');
const selectedEndDate = ref('');
const selectedDateField = ref('created_at');
const selectedTeamId = ref(null);
const selectedStatus = ref('open');

const dateFieldOptions = computed(() => [
  selectedStatus.value === 'resolved'
    ? {
        label: t('CHAT_LIST.HISTORY_FILTER.DATE_FIELDS.resolved_at'),
        value: 'resolved_at',
      }
    : {
        label: t('CHAT_LIST.HISTORY_FILTER.DATE_FIELDS.created_at'),
        value: 'created_at',
      },
  ...(selectedStatus.value === 'resolved'
    ? []
    : [
        {
          label: t('CHAT_LIST.HISTORY_FILTER.DATE_FIELDS.last_activity_at'),
          value: 'last_activity_at',
        },
      ]),
]);

const statusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

const hasActiveFilter = computed(() =>
  Boolean(
    props.filters.startDate ||
      props.filters.endDate ||
      props.filters.teamId ||
      (props.filters.status && props.filters.status !== 'open')
  )
);

const syncFilters = () => {
  selectedStatus.value = props.filters.status || props.activeStatus || 'open';
  selectedStartDate.value = props.filters.startDate || '';
  selectedEndDate.value = props.filters.endDate || '';
  selectedDateField.value =
    selectedStatus.value === 'resolved'
      ? 'resolved_at'
      : props.filters.dateField || 'created_at';
  selectedTeamId.value = props.filters.teamId || null;
};

const closeDropdown = () => toggleDropdown(false);

const handleApply = () => {
  const startDate = selectedStartDate.value || undefined;
  const endDate = selectedEndDate.value || selectedStartDate.value || undefined;

  emit('apply', {
    startDate,
    endDate,
    dateField:
      selectedStatus.value === 'resolved'
        ? 'resolved_at'
        : selectedDateField.value,
    teamId: selectedTeamId.value || undefined,
    status: selectedStatus.value === 'open' ? undefined : selectedStatus.value,
  });
  closeDropdown();
};

const handleClear = () => {
  selectedStatus.value = 'open';
  selectedStartDate.value = '';
  selectedEndDate.value = '';
  selectedDateField.value = 'created_at';
  selectedTeamId.value = null;
  emit('clear');
  closeDropdown();
};

watch([() => props.filters, () => props.activeStatus], syncFilters, {
  immediate: true,
  deep: true,
});
watch(selectedStatus, status => {
  selectedDateField.value =
    status === 'resolved' ? 'resolved_at' : 'created_at';
});
</script>

<template>
  <div class="relative">
    <NextButton
      v-tooltip.right="$t('CHAT_LIST.HISTORY_FILTER.TOOLTIP')"
      icon="i-lucide-calendar-search"
      slate
      xs
      :faded="!hasActiveFilter"
      @click="toggleDropdown()"
    />
    <span
      v-if="hasActiveFilter"
      class="absolute size-2 rounded-full bg-n-ruby-9 ltr:right-0 rtl:left-0 top-0"
    />
    <div
      v-if="showDropdown"
      v-on-click-outside="closeDropdown"
      class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-80 rounded-xl p-4 absolute z-40 top-full"
      :class="{
        'ltr:left-0 rtl:right-0': !isOnExpandedLayout,
        'ltr:right-0 rtl:left-0': isOnExpandedLayout,
      }"
    >
      <div class="space-y-4">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ $t('CHAT_LIST.HISTORY_FILTER.TITLE') }}
          </span>
        </div>

        <label class="block space-y-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('CHAT_LIST.HISTORY_FILTER.DATE_RANGE') }}
          </span>
          <div class="grid grid-cols-2 gap-2">
            <label class="block space-y-1">
              <span class="text-xs text-n-slate-10">
                {{ $t('CHAT_LIST.HISTORY_FILTER.START_DATE') }}
              </span>
              <input
                v-model="selectedStartDate"
                type="date"
                class="w-full h-8 px-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak text-n-slate-12"
              />
            </label>
            <label class="block space-y-1">
              <span class="text-xs text-n-slate-10">
                {{ $t('CHAT_LIST.HISTORY_FILTER.END_DATE') }}
              </span>
              <input
                v-model="selectedEndDate"
                type="date"
                class="w-full h-8 px-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak text-n-slate-12"
                :min="selectedStartDate || undefined"
              />
            </label>
          </div>
        </label>

        <label class="block space-y-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('CHAT_LIST.HISTORY_FILTER.STATUS') }}
          </span>
          <select
            v-model="selectedStatus"
            class="w-full h-8 px-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak text-n-slate-12"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="block space-y-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('CHAT_LIST.HISTORY_FILTER.DATE_FIELD') }}
          </span>
          <select
            v-model="selectedDateField"
            class="w-full h-8 px-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak text-n-slate-12"
          >
            <option
              v-for="option in dateFieldOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="block space-y-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('CHAT_LIST.HISTORY_FILTER.TEAM') }}
          </span>
          <select
            v-model="selectedTeamId"
            class="w-full h-8 px-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak text-n-slate-12"
          >
            <option :value="null">
              {{ $t('CHAT_LIST.HISTORY_FILTER.ALL_TEAMS') }}
            </option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
        </label>

        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            class="h-8 px-3 text-sm rounded-md text-n-slate-11 hover:text-n-slate-12"
            @click="handleClear"
          >
            {{ $t('CHAT_LIST.HISTORY_FILTER.CLEAR') }}
          </button>
          <button
            type="button"
            class="h-8 px-3 text-sm font-medium rounded-md text-white bg-n-brand hover:bg-n-brand/90"
            @click="handleApply"
          >
            {{ $t('CHAT_LIST.HISTORY_FILTER.APPLY') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
